import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import api from '../services/api';
function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { a, b, answer: a + b };
}

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessForm, setAccessForm] = useState({
    fullName: '', email: '', dob: '', gender: '', leagueAndCenter: '', captchaAnswer: ''
  });
  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessSuccess, setAccessSuccess] = useState(false);

  const openAccessModal = () => {
    setAccessForm({ fullName: '', email: '', dob: '', gender: '', leagueAndCenter: '', captchaAnswer: '' });
    setCaptcha(generateCaptcha());
    setAccessError(null);
    setAccessSuccess(false);
    setShowAccessModal(true);
  };

  const handleAccessSubmit = async () => {
    const { fullName, email, dob, gender, leagueAndCenter, captchaAnswer } = accessForm;
    if (!fullName || !email || !dob || !gender || !leagueAndCenter) {
      setAccessError('Please fill in all fields.');
      return;
    }
    if (parseInt(captchaAnswer, 10) !== captcha.answer) {
      setAccessError('Incorrect answer to the verification question. Please try again.');
      setCaptcha(generateCaptcha());
      setAccessForm(f => ({ ...f, captchaAnswer: '' }));
      return;
    }
    setAccessLoading(true);
    setAccessError(null);
    try {
      await api.post('/support/account-access-request', { fullName, email, dob, gender, leagueAndCenter });
      setAccessSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Failed to submit request.';
      setAccessError(msg);
    } finally {
      setAccessLoading(false);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-2">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="max-w-7xl mx-auto text-center">
        {/* Logo/Header */}
        <div className="mb-6">
          <img 
            src="/BowlerProLogo1.png" 
            alt="BowlerPro Logo" 
            className="mx-auto max-h-60 w-auto"
          />
          <p className="text-xl text-white max-w-2xl mx-auto">
            {t('welcome.subtitle')}
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 mb-8">
          {/* Player Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 hover:bg-white/15 transition duration-300">
            <div className="text-4xl mb-4">🏅</div>
            <h2 className="text-2xl font-bold text-white mb-4">{t('welcome.playerCard.title')}</h2>
            <p className="text-blue-200 mb-4">
              {t('welcome.playerCard.description')}
            </p>
            <button
              onClick={openAccessModal}
              className="text-xs text-green-300 hover:text-green-100 underline underline-offset-2 mb-5 block text-left transition"
            >
              Bowling in a league in BowlerPro and want to access your profile for the first time?
            </button>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/login/player')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.playerCard.loginButton')}
              </button>
              <button
                onClick={() => navigate('/signup/player')}
                className="w-full bg-transparent border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.playerCard.signupButton')}
              </button>
            </div>
          </div>

          {/* Admin Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 hover:bg-white/15 transition duration-300">
            <div className="text-4xl mb-4">⚙️</div>
            <h2 className="text-2xl font-bold text-white mb-4">{t('welcome.adminCard.title')}</h2>
            <p className="text-blue-200 mb-6">
              {t('welcome.adminCard.description')}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login/admin')}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.adminCard.loginButton')}
              </button>
              <button
                onClick={() => navigate('/signup/admin')}
                className="w-full bg-transparent border-2 border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.adminCard.signupButton')}
              </button>
            </div>
          </div>

          {/* Bookie Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 hover:bg-white/15 transition duration-300">
            <div className="text-4xl mb-4">🪙</div>
            <h2 className="text-2xl font-bold text-white mb-4">{t('welcome.bookieCard.title')}</h2>
            <p className="text-blue-200 mb-2">
              {t('welcome.bookieCard.description')}
            </p>
            <button
              onClick={() => navigate('/live/side-action')}
              className="text-xs text-yellow-300 hover:text-yellow-100 underline underline-offset-2 mb-5 block text-left transition"
            >
              Want to view the live scores of side action leagues?
            </button>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login/bookie')}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.bookieCard.loginButton')}
              </button>
              <button
                onClick={() => navigate('/signup/bookie')}
                className="w-full bg-transparent border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.bookieCard.signupButton')}
              </button>
            </div>
          </div>

          {/* Scout Card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 hover:bg-white/15 transition duration-300">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-2xl font-bold text-white mb-4">{t('welcome.scoutCard.title')}</h2>
            <p className="text-blue-200 mb-6">
              {t('welcome.scoutCard.description')}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/login/scout')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.scoutCard.loginButton')}
              </button>
              <button
                onClick={() => navigate('/signup/scout')}
                className="w-full bg-transparent border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white font-semibold py-3 px-6 rounded-lg transition duration-200"
              >
                {t('welcome.scoutCard.signupButton')}
              </button>
            </div>
          </div>
        </div>

       

        {/* Features 
        <div className="grid md:grid-cols-4 gap-6 text-white">
          <div className="text-center">
           
            <h3 className="font-semibold">{t('welcome.features.tracking.title')}</h3>
            <p className="text-sm text-blue-200">{t('welcome.features.tracking.description')}</p>
          </div>
          <div className="text-center">
            
            <h3 className="font-semibold">{t('welcome.features.tournaments.title')}</h3>
            <p className="text-sm text-blue-200">{t('welcome.features.tournaments.description')}</p>
          </div>
          <div className="text-center">
          
            <h3 className="font-semibold">{t('welcome.features.payments.title')}</h3>
            <p className="text-sm text-blue-200">{t('welcome.features.payments.description')}</p>
          </div>
          <div className="text-center">
           
            <h3 className="font-semibold">{t('welcome.features.calendar.title')}</h3>
            <p className="text-sm text-blue-200">{t('welcome.features.calendar.description')}</p>
          </div>
        </div>*/}

        {/* YouTube Tutorial Banner */}
        <a
          href="https://www.youtube.com/@RageTechLLC"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex items-center gap-4 mb-10 mx-auto overflow-hidden bg-white/10 hover:bg-white/20 border border-white/20 hover:border-red-400/60 backdrop-blur-lg rounded-xl px-6 py-4 transition duration-300"
        >
          {/* YouTube icon */}
          <div className="relative z-10 flex-shrink-0 w-12 h-12 bg-red-600 group-hover:bg-red-500 rounded-lg flex items-center justify-center transition duration-200">
            <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </div>

          {/* Text */}
          <div className="relative z-10 text-left">
            <p className="text-white font-semibold text-md leading-tight">New to BowlerPro? Watch our Tutorials!</p>
            <p className="text-red-300 group-hover:text-red-200 text-sm mt-0.5 transition duration-200">@RageTechLLC on YouTube 👆</p>
          </div>

          {/* Thumbnail window — right side, fades in from transparent */}
          <div className="absolute inset-y-0 right-0 w-96 pointer-events-none">
            {/* gradient fade so image blends into the card */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-800/60 via-blue-800/10 to-transparent z-10" />
            <img
              src="/youtubeminiature.png"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-65 transition duration-300"
            />
          </div>
        </a>
      </div>
    </div>

      {/* Account Access Request Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowAccessModal(false)}>
          <div
            className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-xl p-6 w-full max-w-md border border-white/20 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-white">Account Access Request</h2>
              <button onClick={() => setShowAccessModal(false)} className="text-white/40 hover:text-white text-xl leading-none">✕</button>
            </div>

            {accessSuccess ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <p className="text-white font-semibold text-lg mb-2">Request Submitted!</p>
                <p className="text-blue-200 text-sm mb-6">We received your request and will reach out to the email you provided to help you access your account.</p>
                <button
                  onClick={() => setShowAccessModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-blue-200 text-sm mb-5">
                  Fill out the form below and we'll locate your account and help you get access.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={accessForm.fullName}
                      onChange={e => setAccessForm(f => ({ ...f, fullName: e.target.value }))}
                      placeholder="Your full name"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-400 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">Email *</label>
                    <input
                      type="email"
                      value={accessForm.email}
                      onChange={e => setAccessForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-400 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">Date of Birth *</label>
                    <input
                      type="date"
                      value={accessForm.dob}
                      onChange={e => setAccessForm(f => ({ ...f, dob: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">Gender *</label>
                    <select
                      value={accessForm.gender}
                      onChange={e => setAccessForm(f => ({ ...f, gender: e.target.value }))}
                      className="w-full px-3 py-2 bg-blue-900 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400 text-sm"
                    >
                      <option value="">Select gender</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">League name & Bowling Center *</label>
                    <textarea
                      value={accessForm.leagueAndCenter}
                      onChange={e => setAccessForm(f => ({ ...f, leagueAndCenter: e.target.value }))}
                      placeholder="e.g. Tuesday Night Mixed League at Striker Lanes, Miami FL"
                      rows={3}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-400 text-sm resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-blue-200 text-xs font-semibold mb-1">
                      Verification: What is {captcha.a} + {captcha.b}? *
                    </label>
                    <input
                      type="number"
                      value={accessForm.captchaAnswer}
                      onChange={e => setAccessForm(f => ({ ...f, captchaAnswer: e.target.value }))}
                      placeholder="Enter the result"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-400 text-sm"
                    />
                  </div>
                </div>

                {accessError && (
                  <p className="mt-3 text-red-300 text-xs">{accessError}</p>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowAccessModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-lg font-semibold transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAccessSubmit}
                    disabled={accessLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold transition text-sm"
                  >
                    {accessLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Welcome;
